import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Building,
  MapPin,
  Bed,
  Bath,
  Ruler,
  Calendar,
  User,
  Edit,
  Trash2,
  CheckCircle,
  XCircle,
  Wrench,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Property } from "@/types/types";
import { getPropertyById, deleteProperty } from "@/lib/property-service";
import { formatNaira } from "@/lib/utils";

export default function PropertyDetailsPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [property, setProperty] = useState<Property | null>(null);
  const [loading, setLoading] = useState(true);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  useEffect(() => {
    if (id) {
      loadProperty();
    }
  }, [id]);

  async function loadProperty() {
    if (!id) return;

    try {
      setLoading(true);
      const propertyData = await getPropertyById(id);
      setProperty(propertyData);
    } catch (error) {
      console.error("Error loading property:", error);
      toast.error("Failed to load property details");
      navigate("/landlord/dashboard/properties");
    } finally {
      setLoading(false);
    }
  }

  const handleDelete = async () => {
    if (!property) return;

    try {
      await deleteProperty(property.id);
      toast.success("Property deleted successfully");
      navigate("/landlord/dashboard/properties");
    } catch (error) {
      console.error("Error deleting property:", error);
      toast.error("Failed to delete property");
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "occupied":
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case "vacant":
        return <XCircle className="h-4 w-4 text-yellow-500" />;
      case "maintenance":
        return <Wrench className="h-4 w-4 text-orange-500" />;
      default:
        return null;
    }
  };

  const getStatusBadge = (status: string) => {
    const variants: Record<string, "default" | "secondary" | "destructive"> = {
      occupied: "default",
      vacant: "secondary",
      maintenance: "destructive",
    };

    return (
      <Badge variant={variants[status] || "default"} className="capitalize">
        {status}
      </Badge>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center space-y-4">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="text-muted-foreground">Loading property details...</p>
        </div>
      </div>
    );
  }

  if (!property) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center space-y-4">
          <Building className="h-16 w-16 mx-auto text-muted-foreground" />
          <h2 className="text-2xl font-bold">Property Not Found</h2>
          <p className="text-muted-foreground">
            The property you're looking for doesn't exist or has been removed.
          </p>
          <Button onClick={() => navigate("/landlord/dashboard/properties")}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Properties
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate("/landlord/dashboard/properties")}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          <div>
            <h1 className="text-3xl font-bold tracking-tight">
              {property.title}
            </h1>
            <p className="text-muted-foreground flex items-center gap-2 mt-1">
              <MapPin className="h-4 w-4" />
              {property.address}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={() => navigate(`/landlord/properties/${property.id}/edit`)}
          >
            <Edit className="h-4 w-4 mr-2" />
            Edit
          </Button>
          <Button
            variant="destructive"
            onClick={() => setIsDeleteDialogOpen(true)}
          >
            <Trash2 className="h-4 w-4 mr-2" />
            Delete
          </Button>
        </div>
      </div>

      {/* Property Images */}
      {property.imageUrls && property.imageUrls.length > 0 && (
        <Card>
          <CardContent className="p-0">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-6">
              {property.imageUrls.slice(0, 4).map((url, index) => (
                <img
                  key={index}
                  src={url}
                  alt={`${property.title} - ${index + 1}`}
                  className={`w-full h-64 object-cover rounded-lg ${
                    index === 0 ? "md:col-span-2 h-96" : ""
                  }`}
                />
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid gap-6 md:grid-cols-3">
        {/* Main Details */}
        <Card className="md:col-span-2">
          <CardHeader>
            <CardTitle>Property Details</CardTitle>
            <CardDescription>
              Complete information about this property
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Basic Info */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="space-y-1">
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Building className="h-4 w-4" />
                  <span className="text-sm">Type</span>
                </div>
                <p className="font-medium capitalize">
                  {property.type.replace("-", " ")}
                </p>
              </div>
              <div className="space-y-1">
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Bed className="h-4 w-4" />
                  <span className="text-sm">Bedrooms</span>
                </div>
                <p className="font-medium">{property.bedrooms}</p>
              </div>
              <div className="space-y-1">
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Bath className="h-4 w-4" />
                  <span className="text-sm">Bathrooms</span>
                </div>
                <p className="font-medium">{property.bathrooms}</p>
              </div>
              <div className="space-y-1">
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Ruler className="h-4 w-4" />
                  <span className="text-sm">Area</span>
                </div>
                <p className="font-medium">{property.area} m²</p>
              </div>
            </div>

            <Separator />

            {/* Description */}
            {property.description && (
              <>
                <div>
                  <h3 className="font-semibold mb-2">Description</h3>
                  <p className="text-muted-foreground">
                    {property.description}
                  </p>
                </div>
                <Separator />
              </>
            )}

            {/* Amenities */}
            {property.amenities && property.amenities.length > 0 && (
              <>
                <div>
                  <h3 className="font-semibold mb-3">Amenities</h3>
                  <div className="flex flex-wrap gap-2">
                    {property.amenities.map((amenity, index) => (
                      <Badge key={index} variant="secondary">
                        {amenity}
                      </Badge>
                    ))}
                  </div>
                </div>
                <Separator />
              </>
            )}

            {/* Multi-unit Info */}
            {property.structure === "multi-unit" && (
              <>
                <div>
                  <h3 className="font-semibold mb-3">Building Information</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-muted-foreground">
                        Total Floors
                      </p>
                      <p className="font-medium">{property.totalFloors}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">
                        Total Units
                      </p>
                      <p className="font-medium">{property.totalUnits}</p>
                    </div>
                  </div>
                  {property.buildingFacilities &&
                    property.buildingFacilities.length > 0 && (
                      <div className="mt-4">
                        <p className="text-sm text-muted-foreground mb-2">
                          Building Facilities
                        </p>
                        <div className="flex flex-wrap gap-2">
                          {property.buildingFacilities.map(
                            (facility, index) => (
                              <Badge key={index} variant="outline">
                                {facility}
                              </Badge>
                            )
                          )}
                        </div>
                      </div>
                    )}
                </div>
                <Separator />

                {/* Units List */}
                <div>
                  <h3 className="font-semibold mb-3">
                    Units ({property.units?.length || 0})
                  </h3>
                  {property.units && property.units.length > 0 ? (
                    <div className="space-y-3">
                      {property.units
                        .sort((a, b) => {
                          if (a.floor !== b.floor) return a.floor - b.floor;
                          return a.name.localeCompare(b.name);
                        })
                        .map((unit) => (
                          <div
                            key={unit.id}
                            className="p-3 border rounded-lg flex items-center justify-between"
                          >
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <p className="font-medium">{unit.name}</p>
                                <Badge
                                  variant={
                                    unit.status === "vacant"
                                      ? "secondary"
                                      : unit.status === "occupied"
                                      ? "default"
                                      : "outline"
                                  }
                                >
                                  {unit.status}
                                </Badge>
                              </div>
                              <div className="flex items-center gap-4 text-sm text-muted-foreground">
                                <span>Floor {unit.floor}</span>
                                <span>{unit.bedrooms} bed</span>
                                <span>{unit.bathrooms} bath</span>
                                <span>{unit.area}m²</span>
                              </div>
                            </div>
                            <div className="text-right">
                              <p className="font-semibold">
                                {formatNaira(unit.rent)}/mo
                              </p>
                              {unit.tenantName && (
                                <p className="text-xs text-muted-foreground mt-1">
                                  {unit.tenantName}
                                </p>
                              )}
                            </div>
                          </div>
                        ))}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground">
                      No units added yet
                    </p>
                  )}
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Rental Info */}
          <Card>
            <CardHeader>
              <CardTitle>Rental Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <div className="flex items-center gap-2 text-muted-foreground mb-1">
                  <span className="text-sm">Monthly Rent</span>
                </div>
                {property.structure === "multi-unit" &&
                property.units &&
                property.units.length > 0 ? (
                  <div>
                    <p className="text-2xl font-bold">
                      {formatNaira(
                        Math.min(...property.units.map((u) => u.rent))
                      )}{" "}
                      -{" "}
                      {formatNaira(
                        Math.max(...property.units.map((u) => u.rent))
                      )}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      Price range across {property.units.length} units
                    </p>
                  </div>
                ) : (
                  <p className="text-2xl font-bold">
                    {formatNaira(property.rent)}
                  </p>
                )}
              </div>
              {property.securityDeposit && (
                <div>
                  <p className="text-sm text-muted-foreground">
                    Security Deposit
                  </p>
                  <p className="font-medium">
                    {formatNaira(property.securityDeposit)}
                  </p>
                </div>
              )}
              {property.leaseDuration && (
                <div>
                  <p className="text-sm text-muted-foreground">
                    Lease Duration
                  </p>
                  <p className="font-medium">{property.leaseDuration} months</p>
                </div>
              )}
              {property.paymentTerms && (
                <div>
                  <p className="text-sm text-muted-foreground">Payment Terms</p>
                  <p className="font-medium capitalize">
                    {property.paymentTerms}
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Status */}
          <Card>
            <CardHeader>
              <CardTitle>Property Status</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">
                  Current Status
                </span>
                <div className="flex items-center gap-2">
                  {getStatusIcon(property.status)}
                  {getStatusBadge(property.status)}
                </div>
              </div>
              {property.status === "occupied" && (
                <>
                  <Separator />
                  <div>
                    <div className="flex items-center gap-2 text-muted-foreground mb-1">
                      <User className="h-4 w-4" />
                      <span className="text-sm">Current Tenant</span>
                    </div>
                    <p className="font-medium">{property.tenant || "N/A"}</p>
                  </div>
                  {property.leaseEnd && (
                    <div>
                      <div className="flex items-center gap-2 text-muted-foreground mb-1">
                        <Calendar className="h-4 w-4" />
                        <span className="text-sm">Lease Ends</span>
                      </div>
                      <p className="font-medium">{property.leaseEnd}</p>
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>

          {/* Approval Status */}
          {property.approvalStatus && (
            <Card>
              <CardHeader>
                <CardTitle>Approval Status</CardTitle>
              </CardHeader>
              <CardContent>
                <Badge
                  variant={
                    property.approvalStatus === "approved"
                      ? "default"
                      : property.approvalStatus === "pending"
                      ? "secondary"
                      : "destructive"
                  }
                  className="capitalize"
                >
                  {property.approvalStatus}
                </Badge>
                {property.approvalStatus === "pending" && (
                  <p className="text-sm text-muted-foreground mt-2">
                    This property is pending admin approval before it can be
                    listed.
                  </p>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Property</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{property.title}"? This action
              cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsDeleteDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDelete}>
              Delete Property
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
